/**
 * usePayRunViews (PR8.5) — canonical hook for resolving every job's
 * current PayRunView + variance signals + reconciliation findings.
 *
 * Extracted verbatim from PayRunsCockpitPage so any surface (Accounting
 * rollup, JobCostBreakdownPanel, Job Detail) can consume the same join
 * without re-implementing the multi-context wiring. Pure read.
 *
 * Rules:
 *   • No writes. No mutations. No localStorage.
 *   • Returns the same shapes the cockpit consumes today (no behavior change).
 *   • Single source for: rows, ready/held partitioning, recorded-key set,
 *     labor variance signals, drift reconciliations, variance rollup.
 */
import { useMemo } from "react";
import { useAppData } from "@/data/AppDataContext";
import { getJobPayRun, type PayRunView } from "./getJobPayRun";
import { getPayProfile } from "@/data/contractorPayProfile";
import { deriveAllHoldbackStates } from "@/data/contractor/networkExecution/holdback/deriveState";
import { deriveAllDisputes } from "@/data/contractor/networkExecution/dispute/dispute";
import { estimateJobSpecLabor } from "./estimateJobSpecLabor";
import { getEffectiveLaborActual, toLaborCostActualInput, type LaborActualRecord } from "./laborActuals";
import { getEffectiveFieldAdds, type FieldAddSubmission } from "./fieldAdds";
import type { PayRunRecord } from "./recordPayRun";
import { getPayRunHoldAge, type PayRunHoldAge } from "./getPayRunHoldAge";
import {
  getLaborVarianceSignal,
  getPayRunReconciliation,
  getVarianceRollup,
  type LaborVarianceSignal,
  type PayRunReconciliation,
  type VarianceRollup,
} from "./varianceAnalysis";


export interface PayRunRow {
  job: { id: string; customerName: string };
  view: PayRunView;
  contractorId?: string;
  estimatedLabor?: number | null;
  /** Strict hold age — null if the row isn't held or if any reason lacks a timestamp. */
  heldAge?: PayRunHoldAge | null;
}

export interface PayRunViewsResult {
  rows: PayRunRow[];
  rowByJob: Map<string, PayRunRow>;
  readyRows: PayRunRow[];
  heldRows: PayRunRow[];
  /** `${jobId}::${contractorId}::${wrapRecordId}` keys for already-released runs. */
  recordedKey: Set<string>;
  /** Stable key builder so callers compose Ready filtering identically. */
  buildRecordedKey: (jobId: string, contractorId: string | undefined) => string;
  laborSignals: Array<{ row: PayRunRow; signal: LaborVarianceSignal }>;
  reconciliations: PayRunReconciliation[];
  varianceRollup: VarianceRollup;
}


export function usePayRunViews(): PayRunViewsResult {
  const appData = useAppData();
  const {
    jobs, contractors, payProfiles, installerWrapRecords,
    callbacks, holdbackLedger, disputeEvents,
  } = appData;
  // These fields exist in the full AppDataContext (origin/main) but not yet wired in this branch
  const payRunRecords: PayRunRecord[] = (appData as any).payRunRecords ?? [];
  const laborActualRecords: LaborActualRecord[] = (appData as any).laborActualRecords ?? [];
  const fieldAddSubmissions: FieldAddSubmission[] = (appData as any).fieldAddSubmissions ?? [];

  const contractorById = useMemo(
    () => new Map(contractors.map((c) => [c.id, c])),
    [contractors],
  );

  const callbacksByJob = useMemo(() => {
    const m = new Map<string, typeof callbacks>();
    for (const c of callbacks) {
      const arr = m.get(c.jobId) ?? [];
      arr.push(c);
      m.set(c.jobId, arr);
    }
    return m;
  }, [callbacks]);

  const holdbackStates = useMemo(() => deriveAllHoldbackStates(holdbackLedger), [holdbackLedger]);
  const disputeStates = useMemo(() => deriveAllDisputes(disputeEvents), [disputeEvents]);

  const recordedKey = useMemo(
    () => new Set(payRunRecords.map((r) => `${r.jobId}::${r.partyId ?? ""}::${r.wrapRecordId ?? ""}`)),
    [payRunRecords],
  );

  const rows: PayRunRow[] = useMemo(() => {
    const out: PayRunRow[] = [];
    for (const job of jobs) {
      const wrap = installerWrapRecords.find((w) => w.jobId === job.id);
      if (!wrap && !job.qaSubmitted) continue;

      const contractorId = job.assignedSubcontractorId;
      const contractor = contractorId ? contractorById.get(contractorId) : undefined;
      const payProfile = contractor ? getPayProfile(payProfiles, contractor.id) : undefined;

      const estimate = estimateJobSpecLabor(job.boundJobSpec);
      const actualRec = getEffectiveLaborActual(job.id, laborActualRecords);
      const laborActual = actualRec ? toLaborCostActualInput(actualRec) : undefined;

      const fieldAdds = getEffectiveFieldAdds(job.id, contractorId ?? null, fieldAddSubmissions);

      const jobCallbacks = callbacksByJob.get(job.id) ?? [];
      const jobHoldbacks = holdbackStates.filter((h) => h.jobId === job.id);
      const jobDisputes = disputeStates.filter((d) => d.jobId === job.id);
      const jobPartyFieldAddSubs = fieldAddSubmissions.filter(
        (s) => s.jobId === job.id && (s.partyId ?? null) === (contractorId ?? null),
      );

      const view = getJobPayRun({
        job,
        contractor,
        payProfile,
        wrapRecord: wrap,
        callbacks: jobCallbacks,
        holdbacks: jobHoldbacks,
        disputes: jobDisputes,
        laborEstimate: estimate ?? undefined,
        laborActual,
        fieldAdds,
      });
      const heldAge = !view.releasable
        ? getPayRunHoldAge({
            view,
            wrapRecord: wrap,
            callbacks: jobCallbacks,
            holdbacks: jobHoldbacks,
            disputes: jobDisputes,
            fieldAddSubmissions: jobPartyFieldAddSubs,
          })
        : null;
      out.push({
        job: { id: job.id, customerName: job.customerName },
        view,
        contractorId,
        estimatedLabor: estimate?.estimatedDollars ?? null,
        heldAge,
      });

    }
    return out;
  }, [
    jobs, installerWrapRecords, contractorById, payProfiles,
    callbacksByJob, holdbackStates, disputeStates,
    laborActualRecords, fieldAddSubmissions,
  ]);

  const rowByJob = useMemo(() => {
    const m = new Map<string, PayRunRow>();
    for (const r of rows) m.set(r.job.id, r);
    return m;
  }, [rows]);

  const buildRecordedKey = (jobId: string, contractorId: string | undefined) => {
    const wrapId = installerWrapRecords.find((w) => w.jobId === jobId)?.id ?? "";
    return `${jobId}::${contractorId ?? ""}::${wrapId}`;
  };

  const readyRows = useMemo(
    () => rows.filter(
      (r) => r.view.releasable && !recordedKey.has(buildRecordedKey(r.job.id, r.contractorId)),
    ),
    [rows, recordedKey, installerWrapRecords],
  );
  const heldRows = useMemo(() => rows.filter((r) => !r.view.releasable), [rows]);

  const laborSignals = useMemo(
    () =>
      rows
        .map((r) => ({ row: r, signal: getLaborVarianceSignal(r.view) }))
        .filter((x) => x.signal.comparable),
    [rows],
  );

  const reconciliations = useMemo(
    () =>
      payRunRecords
        .map((rec) => {
          const current = rowByJob.get(rec.jobId)?.view;
          return current ? getPayRunReconciliation(rec, current) : null;
        })
        .filter((x): x is PayRunReconciliation => x !== null && !x.reconciled),
    [payRunRecords, rowByJob],
  );

  const varianceRollup = useMemo(
    () => getVarianceRollup(laborSignals.map((s) => s.signal), reconciliations),
    [laborSignals, reconciliations],
  );

  return {
    rows, rowByJob, readyRows, heldRows,
    recordedKey, buildRecordedKey,
    laborSignals, reconciliations, varianceRollup,
  };
}
